import { motion } from "framer-motion";
import { Video, AudioLines, Gauge, ArrowRight } from "lucide-react";
import { scrollToId } from "../../lib/scroll";
import { useSection } from "../../lib/content";
import { trackCta } from "../../lib/track";
import { Reveal, EASE } from "./Reveal";

const CHIP_ICONS = [Video, AudioLines, Gauge];

export const BenefitsBanner = () => {
  const c = useSection("banner");

  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden bg-[#1a1a1a] grid lg:grid-cols-[1.25fr_1fr] min-h-[340px]">
            <div className="relative flex flex-col justify-center p-10 lg:p-16">
              <p className="font-mono text-xs uppercase tracking-[0.18em] font-semibold text-[#FF5050] mb-5">
                {c.eyebrow}
              </p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-[28px] xl:text-4xl font-bold tracking-tight text-white leading-[1.15] lg:whitespace-nowrap">
                {c.title}
              </h2>
              <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed max-w-md">
                {c.paragraph}
              </p>
              <div className="mt-8 flex flex-wrap lg:flex-nowrap gap-3">
                {c.chips.map((chip, i) => {
                  const Icon = CHIP_ICONS[i % CHIP_ICONS.length];
                  return (
                    <span key={chip} className="inline-flex items-center gap-2 whitespace-nowrap border border-white/15 bg-white/5 rounded-full px-4 py-2 text-sm text-slate-200">
                      <Icon className="w-4 h-4 text-[#FF5050]" />
                      {chip}
                    </span>
                  );
                })}
              </div>
              <button
                data-testid="benefits-banner-cta"
                onClick={() => { trackCta("benefits-banner"); scrollToId("contact"); }}
                className="group mt-9 inline-flex items-center gap-2 text-[#FF5050] font-semibold text-sm hover:text-[#FF7A7A] transition-colors w-fit"
              >
                {c.ctaText}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
            <div className="relative min-h-[260px] lg:min-h-0">
              <motion.img
                initial={{ opacity: 0, scale: 1.05 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: EASE }}
                src={c.image}
                alt="Enterprise team running a hybrid video meeting with HP Poly Gen 2"
                className="absolute inset-0 w-full h-full object-cover lg:[clip-path:polygon(12%_0,100%_0,100%_100%,0%_100%)]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/60 via-transparent to-transparent lg:bg-none" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
